import { Settings, BookOpen, Zap, Rocket, ShieldCheck, Layers } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";

const icons = [Settings, BookOpen, Zap, Rocket, ShieldCheck, Layers];

const CapabilityProofsSection = () => {
  const { ref, isVisible } = useScrollAnimation();
  const { t, tObject } = useLanguage();
  const items = tObject<Array<{ title: string; description: string }>>('aiAgentPlattform.capabilities.items');

  return (
    <section 
      ref={ref as React.RefObject<HTMLElement>}
      className={cn(
        "py-16 md:py-24 bg-secondary/30 transition-all duration-700 ease-out",
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-4 text-sm font-semibold text-primary uppercase tracking-wider">{t('aiAgentPlattform.capabilities.badge')}</Badge>
            
            <h2 className="text-3xl md:text-4xl font-semibold mb-2">
              <span className="text-foreground">{t('aiAgentPlattform.capabilities.titleLine1')}</span>
              <br />
              <span className="text-foreground">{t('aiAgentPlattform.capabilities.titleLine2')}</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {items.map((cap, index) => {
              const Icon = icons[index];
              return (
                <div
                  key={index}
                  className={cn(
                    "bg-white rounded-2xl p-6 border border-border shadow-[0_4px_24px_rgba(0,196,180,0.08)] transition-all",
                    isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                  )}
                  style={{ transitionDelay: isVisible ? `${index * 100}ms` : "0ms", transitionDuration: "500ms" }}
                >
                  <div className="w-12 h-12 rounded-xl bg-chathero-mint/10 border border-chathero-mint/20 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-chathero-mint" />
                  </div>
                  <h3 className="text-lg font-semibold text-primary mb-1">{cap.title}</h3>
                  <p className="text-muted-foreground text-sm">{cap.description}</p>
                </div>
              );
            })}
          </div>

          <div 
            className={cn(
              "bg-chathero-mint/10 rounded-2xl p-6 border border-chathero-mint/20 transition-all duration-500",
              isVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"
            )}
            style={{ transitionDelay: isVisible ? "600ms" : "0ms" }}
          >
            <p className="text-lg font-semibold text-primary text-center">
              <span className="text-chathero-orange">{t('aiAgentPlattform.capabilities.resultLabel')}</span>{" "}
              {t('aiAgentPlattform.capabilities.result')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CapabilityProofsSection;
